import React, { useRef, useState, useEffect, useMemo } from "react";
import { SplitRowHalf } from "../../../../assets/components/ButtonGroup";
import { HeaderIconL } from "../../../../assets/components/HeaderGroup";
import { addAppMessageListener, postToApp } from "../../../../bridges/appBridge";
import arrowleft from "../../../../assets/icons/Arrow/left.svg";

export default function Step1({
  data = {},
  image,
  setImage,
  onNext,
  onBack,
}) {
  // ENV 판별
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const isAndroid = useMemo(() => /Android/i.test(ua), [ua]);
  const isRN = typeof window !== "undefined" && !!window.ReactNativeWebView;
  const isAndroidRN = isAndroid && isRN;
  const isWeb = !isRN;

  const fileInputRef = useRef(null);
  const triggeredRef = useRef(false);

  const [imageFile, setImageFile] = useState(image?.imageFile || null);
  const [imagePreview, setImagePreview] = useState(image?.imagePreview || null);

  const canGoNext = !!imageFile;
// console.log(data)
  // 상위 Gallery와 동기화
  useEffect(() => {
    if (!setImage) return;
    setImage((prev) => ({
      ...prev,
      imageFile: imageFile || prev.imageFile || null,
      imagePreview: imagePreview || prev.imagePreview || null,
    }));
  }, [imageFile, imagePreview, setImage]);

  // 네이티브 메시지 리스너
  useEffect(() => {
    if (!isRN) return;

    const off = addAppMessageListener((msg) => {
      const t = msg?.type;
      const p = msg?.payload || {};

      if (t === "PICK_IMAGE_RESULT" && p?.dataUrl) {
        try {
          setImagePreview(p.dataUrl);
          const [h, b] = p.dataUrl.split(",");
          const mime = h.match(/:(.*?);/)?.[1] || "image/jpeg";
          const bin = atob(b);
          const u8 = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) u8[i] = b.charCodeAt(i);
          const file = new File(
            [new Blob([u8], { type: mime })],
            "picked.jpg",
            { type: mime }
          );
          setImageFile(file);
        } catch (e) {
          alert("이미지 처리에 실패했어요. 다시 시도해주세요.");
        }
      }

      if (t === "PICK_IMAGE_CANCEL") return;
      if (t === "PICK_IMAGE_ERROR") {
        alert(p?.message || "이미지 선택 중 오류가 발생했습니다.");
        return;
      }
    });

    return () => {
      try {
        off?.();
      } catch { }
    };
  }, [isRN]);

  // 레거시 base64 콜백
  useEffect(() => {
    window.receiveCameraImage = function (dataUri) {
      try {
        setImagePreview(dataUri);
        const [h, b] = dataUri.split(",");
        const mime = h.match(/:(.*?);/)?.[1] || "image/jpeg";
        const bin = atob(b);
        const u8 = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
        const file = new File(
          [new Blob([u8], { type: mime })],
          "captured.jpg",
          { type: mime }
        );
        setImageFile(file);
      } catch (err) {
        alert("이미지 처리에 실패했어요.");
      }
    };
  }, []);

  // 첫 진입 시 자동 픽커
  useEffect(() => {
    if (triggeredRef.current) return;
    triggeredRef.current = true;

    if (isAndroidRN && !imageFile) {
      try {
        postToApp({
          type: "OPEN_IMAGE_PICKER",
          payload: { at: Date.now(), reason: "mount_fire" },
        });
      } catch { }
      return;
    }

    if (isWeb && !imageFile) {
      const t = setTimeout(() => {
        try {
          fileInputRef.current?.click();
        } catch { }
      }, 300);
      return () => clearTimeout(t);
    }
  }, [isAndroidRN, isWeb, imageFile]);

  // 웹 파일 선택
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  // 수동 픽커 열기
  const openPickerManually = () => {
    if (isAndroidRN) {
      try {
        postToApp({
          type: "OPEN_IMAGE_PICKER",
          payload: { at: Date.now(), reason: "manual_button" },
        });
      } catch { }
      try {
        window.Android?.openGallery?.();
      } catch { }
    } else {
      fileInputRef.current?.click();
    }
  };

  // 다음 스텝
  const handleNext = () => {
    if (!canGoNext) {
      alert("이미지를 먼저 선택해주세요.");
      return;
    }
    onNext && onNext();
  };

  return (
    <div className="flex flex-col min-h-dvh bg-surface-default">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      <HeaderIconL
        title="사진 업로드"
        icon={arrowleft}
        onIconClick={onBack}
      />

      <main className="flex-1 px-4 pb-4">
        <div className="w-full rounded-2xl overflow-hidden bg-cg-50 flex items-center justify-center">
          {imagePreview ? (
            <img
              src={imagePreview}
              alt="선택한 이미지"
              className="w-auto max-w-full object-cover"
              style={{ maxHeight: "calc(100vh - 274px)" }}
            />
          ) : (
            <span className="py-14 text-text-subtler text-sm">
              이미지를 선택해주세요.
            </span>
          )}
        </div>
      </main>

      <SplitRowHalf
        leftText="이미지 변경하기"
        rightText="다음"
        onLeft={openPickerManually}
        onRight={handleNext}
        leftType="border"
        rightType={canGoNext ? "fill" : "gray"}
      />
    </div>
  );
}
